/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useMemo, useState } from 'react';
import {
  PlanFeatureType, PlanFeatureItem, PlanFeaturesV3, PlanScopedLimits, PlanSegment,
} from '@/features/pricing';
import { loadPlans } from './plans-cache';

export type BillingCycle = 'monthly' | 'yearly';

export type PlanWithFrequency = {
  key: string;
  value: string;
  icon: React.FunctionComponent;
  title: string;
  price: number;
  discount: number;
  features: PlanFeatureItem[];
  limits?: PlanScopedLimits;
  billing_frequency?: string;
  originalId?: string;
  recommended: boolean;
  description: string;
  discountPercent: number;
  users?: number;
  /** Price of one seat, when the catalogue states it outright. */
  pricePerSeat?: number;
  minSeats?: number;
  segment?: PlanSegment;
  availableSpots?: number;
  /** What the catalogue says about buying it today, when it says anything. */
  availability?: string;
  active?: boolean;
  sortOrder?: number;
  monthlyPrice?: number;
  yearlyPrice?: number;
  monthlyId?: string;
  yearlyId?: string;
  /**
   * Free days each variant grants, kept apart the way the prices are.
   *
   * A cycle is a plan of its own in the catalogue, with its own id, and
   * nothing makes the two agree on the trial they offer.
   */
  monthlyTrialDays?: number;
  yearlyTrialDays?: number;
};

export type BasePlanKey = 'emerald' | 'ruby' | 'diamond';

export type SpotsStatus = 'coming_soon' | 'sold_out' | 'unlimited' | 'available';

const UNLIMITED_SPOTS = -1;

/** The states the catalogue states outright, rather than deriving from spots. */
const ANNOUNCED: Record<string, SpotsStatus> = {
  coming_soon: 'coming_soon',
  sold_out: 'sold_out',
};

type AvailabilityFields = Pick<PlanWithFrequency, 'availableSpots' | 'availability'>;

/**
 * Determines the availability status of a plan.
 *
 * `availability` wins when the catalogue states one, because it separates a
 * plan that has not opened from one that filled up — a difference no spot
 * count can express, since both report zero. Plans without it keep deriving
 * their state from the spot count, which is every plan published so far.
 *
 * @param plan - The plan (needs `availableSpots` and `availability`).
 * @returns `coming_soon`, `sold_out`, `unlimited`, or `available`.
 */
export function getSpotsStatus(plan: AvailabilityFields): SpotsStatus {
  const announced = plan.availability ? ANNOUNCED[plan.availability] : undefined;
  if (announced) return announced;
  const spots = plan.availableSpots;
  if (spots === undefined || spots === UNLIMITED_SPOTS) return 'unlimited';
  if (spots <= 0) return 'sold_out';
  return 'available';
}

/**
 * Whether a plan can still be bought.
 *
 * Stated as the set that *can* be selected rather than the one that cannot:
 * written the other way, every state added later defaults to selectable, which
 * is the dangerous half — an unbuyable plan on sale rather than a buyable one
 * withheld.
 *
 * @param plan - The plan (needs `availableSpots` and `availability`).
 * @returns True when the plan is selectable.
 */
export function isPlanSelectable(plan: AvailabilityFields): boolean {
  const status = getSpotsStatus(plan);
  return status === 'available' || status === 'unlimited';
}

/**
 * Whether a segment has anything on sale, as opposed to only announcements.
 *
 * At launch one segment can be entirely unopened. A chooser tab leading to
 * three cards and no way forward is a dead end, so the tab is dropped rather
 * than rendered empty.
 *
 * @param plans - The plans in one segment.
 * @returns True when at least one plan can be bought.
 */
export function segmentHasPlanOnSale(plans: AvailabilityFields[]): boolean {
  return plans.some(isPlanSelectable);
}

const SORT_ORDER_LAST = Number.MAX_SAFE_INTEGER;

/**
 * Sorts plans by `sortOrder`, breaking ties by key.
 *
 * @param plans - The plans to sort.
 * @returns A new, sorted plans array.
 */
export function sortPlansBySortOrder(plans: PlanWithFrequency[]): PlanWithFrequency[] {
  return [...plans].sort((a, b) => {
    const orderA = a.sortOrder ?? SORT_ORDER_LAST;
    const orderB = b.sortOrder ?? SORT_ORDER_LAST;
    if (orderA !== orderB) return orderA - orderB;
    return a.key.localeCompare(b.key);
  });
}

/**
 * Ensures at most one plan is marked recommended (the first in sort order).
 *
 * @param plans - The plans to normalize.
 * @returns Sorted plans with a single recommended flag.
 */
export function normalizeRecommended(plans: PlanWithFrequency[]): PlanWithFrequency[] {
  const ordered = sortPlansBySortOrder(plans);
  const firstRecommendedKey = ordered.find((p) => p.recommended)?.key;
  return ordered.map((p) => ({
    ...p,
    recommended: p.recommended && p.key === firstRecommendedKey,
  }));
}

/**
 * Finds a plan variant matching a billing frequency.
 *
 * @param plans - The candidate plan variants.
 * @param freq - The billing frequency to match.
 * @returns The matching plan, or undefined.
 */
function findByFrequency(plans: PlanFeatureType[], freq: string): PlanFeatureType | undefined {
  return plans.find((p) => p.billing_frequency === freq);
}

/**
 * Picks the base plan variant for a billing cycle, falling back to monthly/first.
 *
 * @param frequencies - The plan variants for one key.
 * @param cycle - The desired billing cycle.
 * @returns The chosen base plan variant.
 */
function pickBasePlanForCycle(frequencies: PlanFeatureType[], cycle: BillingCycle): PlanFeatureType {
  return (
    findByFrequency(frequencies, cycle) ||
    findByFrequency(frequencies, 'monthly') ||
    frequencies[0]
  );
}

/**
 * Converts a features record into an ordered list of feature items.
 *
 * @param features - The plan features record.
 * @returns Feature items with included/none tiers.
 */
export function featuresRecordToItems(
  features: PlanFeaturesV3,
): PlanFeatureItem[] {
  return Object.entries(features).map(([key, enabled]) => ({
    key,
    tier: enabled ? 'included' : 'none',
  }));
}

/**
 * Enriches a base plan with monthly/yearly pricing and derived metadata.
 *
 * @param basePlan - The base plan variant.
 * @param frequencies - All variants sharing the plan key.
 * @returns The enriched plan-with-frequency object.
 */
export function enrichPlan(basePlan: PlanFeatureType, frequencies: PlanFeatureType[]): PlanWithFrequency {
  const monthly = findByFrequency(frequencies, 'monthly');
  const yearly = findByFrequency(frequencies, 'yearly');

  return {
    ...basePlan,
    recommended: basePlan.recommended ?? false,
    description: basePlan.description ?? '',
    discountPercent: yearly?.discount ?? basePlan.discount ?? 0,
    segment: basePlan.segment,
    availableSpots: basePlan.availableSpots,
    availability: basePlan.availability,
    active: basePlan.active,
    sortOrder: basePlan.sortOrder,
    monthlyPrice: monthly?.price,
    yearlyPrice: yearly?.price,
    monthlyTrialDays: monthly?.trialDays,
    yearlyTrialDays: yearly?.trialDays,
    // catalogueId is the catalogue's UUID (what a subscription line and
    // change-plan/checkout both key on); .value is the slug. Using the slug
    // here sent "essencial-solo-yearly" as a plan_id to Payien's
    // change-plan, which has no slug in its catalogue -- it rebuilt the
    // billing snapshot against a plan it couldn't find, silently leaving
    // the subscription's cycle end date and item unit price on the old
    // plan even though the charge itself went through.
    monthlyId: monthly?.catalogueId,
    yearlyId: yearly?.catalogueId,
    features: featuresRecordToItems(basePlan.features || {}),
    limits: basePlan.limits,
    users: basePlan.users,
    pricePerSeat: basePlan.pricePerSeat,
    minSeats: basePlan.minSeats,
  };
}

/**
 * Groups plan variants by their shared plan key.
 *
 * @param plansFeatures - All plan variants.
 * @returns A map from plan key to its variants.
 */
export function groupPlansByKey(plansFeatures: PlanFeatureType[]): Map<string, PlanFeatureType[]> {
  const plansByKey = new Map<string, PlanFeatureType[]>();
  plansFeatures.forEach((plan) => {
    const existing = plansByKey.get(plan.key) || [];
    existing.push(plan);
    plansByKey.set(plan.key, existing);
  });
  return plansByKey;
}

/**
 * Filters plan variants to a single segment.
 *
 * @param plans - All plan variants.
 * @param segment - The segment to keep.
 * @returns Plans in the given segment.
 */
export function filterBySegment(plans: PlanFeatureType[], segment: PlanSegment): PlanFeatureType[] {
  return plans.filter((p) => p.segment === segment);
}

/**
 * Builds the enriched, recommendation-normalized plan list for a billing cycle.
 *
 * @param plansByKey - Plan variants grouped by key.
 * @param cycle - The billing cycle to build for.
 * @returns The enriched plans ready for display.
 */
export function buildEnrichedPlans(
  plansByKey: Map<string, PlanFeatureType[]>,
  cycle: BillingCycle = 'monthly'
): PlanWithFrequency[] {
  const enriched = Array.from(plansByKey.keys()).map((key) => {
    const frequencies = plansByKey.get(key) || [];
    return enrichPlan(pickBasePlanForCycle(frequencies, cycle), frequencies);
  });
  return normalizeRecommended(enriched);
}

/**
 * Resolves a new enriched plan when the user switches billing frequency.
 *
 * @param planKey - The plan key being changed.
 * @param newId - The selected variant id.
 * @param allPlansData - All variants grouped by key.
 * @returns The enriched plan, or null when the variant is missing.
 */
export function changeFrequency(
  planKey: string,
  newId: string,
  allPlansData: Map<string, PlanFeatureType[]>
): PlanWithFrequency | null {
  const allFrequencies = allPlansData.get(planKey) || [];
  const newPlan = allFrequencies.find((p) => p.value === newId);
  if (!newPlan) return null;
  return enrichPlan(newPlan, allFrequencies);
}

interface UsePlansOptions {
  defaultSegment?: PlanSegment;
}

interface UsePlansResult {
  plans: PlanWithFrequency[];
  allPlansData: Map<string, PlanFeatureType[]>;
  isLoading: boolean;
  billingCycle: BillingCycle;
  setBillingCycle: (cycle: BillingCycle) => void;
  updatePlanFrequency: (planKey: string, newId: string) => void;
  segment: PlanSegment;
  setSegment: (segment: PlanSegment) => void;
  availableSegments: PlanSegment[];
}

/**
 * Loads plans and exposes billing-cycle and segment selection state.
 *
 * @param options - Optional default segment.
 * @returns Plans, loading flag, and cycle/segment/frequency setters.
 */
export function usePlans(options: UsePlansOptions = {}): UsePlansResult {
  const { defaultSegment = 'solo' } = options;
  const [allRawPlans, setAllRawPlans] = useState<PlanFeatureType[]>([]);
  const [allPlansData, setAllPlansData] = useState<Map<string, PlanFeatureType[]>>(new Map());
  const [plans, setPlans] = useState<PlanWithFrequency[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [billingCycle, setBillingCycleState] = useState<BillingCycle>('monthly');
  const [segment, setSegmentState] = useState<PlanSegment>(defaultSegment);

  useEffect(() => {
    setSegmentState(defaultSegment);
  }, [defaultSegment]);

  useEffect(() => {
    setIsLoading(true);
    loadPlans()
      .then(({ plans_features }) => {
        setAllRawPlans(plans_features);
        const filtered = filterBySegment(plans_features, defaultSegment);
        const grouped = groupPlansByKey(filtered);
        setAllPlansData(grouped);
        setPlans(buildEnrichedPlans(grouped, billingCycle));
      })
      .finally(() => setIsLoading(false));
  }, [defaultSegment]);

  const rebuildPlans = (seg: PlanSegment, cycle: BillingCycle) => {
    const filtered = filterBySegment(allRawPlans, seg);
    const grouped = groupPlansByKey(filtered);
    setAllPlansData(grouped);
    setPlans(buildEnrichedPlans(grouped, cycle));
  };

  const setBillingCycle = (cycle: BillingCycle) => {
    setBillingCycleState(cycle);
    rebuildPlans(segment, cycle);
  };

  const setSegment = (newSegment: PlanSegment) => {
    setSegmentState(newSegment);
    rebuildPlans(newSegment, billingCycle);
  };

  const updatePlanFrequency = (planKey: string, newId: string) => {
    const updated = changeFrequency(planKey, newId, allPlansData);
    if (updated) {
      setPlans((prev) => prev.map((p) => (p.key === planKey ? updated : p)));
    }
  };

  // A segment whose every plan is an announcement has no path forward, so it
  // is not offered. A segment with no plans at all stays listed: that is an
  // empty catalogue, not a closed segment, and hiding both tabs would leave
  // the step with no chooser and no explanation.
  const availableSegments = useMemo<PlanSegment[]>(() => {
    const segments: PlanSegment[] = ['solo', 'agency'];
    if (!allRawPlans.length) return segments;
    return segments.filter((seg) => {
      const inSegment = filterBySegment(allRawPlans, seg);
      return inSegment.length === 0 || segmentHasPlanOnSale(inSegment);
    });
  }, [allRawPlans]);

  return {
    plans, allPlansData, isLoading, billingCycle, setBillingCycle,
    updatePlanFrequency, segment, setSegment, availableSegments,
  };
}
